import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useAccounts } from "@/features/accounts";
import { useContacts, useCentralize } from "../hooks";
import type { TaxRegisterEntry } from "../types";

function formatCLP(value: number | null): string {
  if (value == null) return "-";
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
  }).format(value);
}

interface EntryAssignment {
  entryId: string;
  accountId: string;
  saveMapping: boolean;
  autoFilled: boolean;
}

interface CentralizeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: TaxRegisterEntry[];
  registerType: "sales" | "purchases";
  accountingConfig: {
    inputVatAccountId?: string;
    outputVatAccountId?: string;
    accountsPayableAccountId?: string;
    accountsReceivableAccountId?: string;
    autoPost?: boolean;
  } | null;
  onSuccess: () => void;
}

export function CentralizeDialog({
  open,
  onOpenChange,
  entries,
  registerType,
  accountingConfig,
  onSuccess,
}: CentralizeDialogProps) {
  const { accounts, isLoading: accountsLoading } = useAccounts();
  const { lookupByTaxIds } = useContacts();
  const { centralize, isCentralizing } = useCentralize();

  const [assignments, setAssignments] = useState<EntryAssignment[]>([]);
  const [createAsDraft, setCreateAsDraft] = useState(
    !accountingConfig?.autoPost,
  );
  const [error, setError] = useState<string | null>(null);

  const isPurchase = registerType === "purchases";

  // Filter accounts by type for the selector
  const selectableAccounts = (accounts ?? []).filter((a) => {
    if (a.isHeader || !a.isActive) return false;
    if (isPurchase) return a.accountType === "EXPENSE";
    return a.accountType === "REVENUE";
  });

  // Initialize assignments when dialog opens, then enhance with contact mappings
  useEffect(() => {
    if (!open || entries.length === 0) return;

    // Immediately set assignments so the form is usable
    const initial = entries.map((entry) => ({
      entryId: entry.id,
      accountId: "",
      saveMapping: false,
      autoFilled: false,
    }));
    setAssignments(initial);

    // Then try to load contact mappings to auto-fill accounts
    const taxIds = [
      ...new Set(entries.map((e) => e.partyTaxId).filter(Boolean)),
    ] as string[];
    if (taxIds.length === 0) return;

    lookupByTaxIds(taxIds)
      .then((contactMap) => {
        setAssignments(
          entries.map((entry) => {
            const contact = entry.partyTaxId
              ? contactMap[entry.partyTaxId]
              : null;
            const defaultAccount = isPurchase
              ? contact?.defaultExpenseAccountId
              : contact?.defaultRevenueAccountId;

            return {
              entryId: entry.id,
              accountId: defaultAccount ?? "",
              saveMapping: false,
              autoFilled: !!defaultAccount,
            };
          }),
        );
      })
      .catch(() => {
        // Contact lookup failed — assignments already initialized, user can pick manually
      });
  }, [open, entries, isPurchase, lookupByTaxIds]);

  const configAccountLabel = (accountId: string | undefined): string => {
    if (!accountId) return "No configurado";
    const acct = (accounts ?? []).find((a) => a.id === accountId);
    return acct ? `${acct.code} - ${acct.name}` : accountId;
  };

  const allAssigned =
    assignments.length > 0 && assignments.every((a) => a.accountId);

  const handleCentralize = async () => {
    setError(null);
    try {
      await centralize(
        assignments.map((a) => ({
          entryId: a.entryId,
          expenseOrRevenueAccountId: a.accountId,
          saveContactMapping: a.saveMapping,
        })),
        createAsDraft,
      );
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al centralizar");
    }
  };

  // When user selects an account for one entry, auto-fill same RUT entries
  const handleAccountChange = (index: number, accountId: string) => {
    setAssignments((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, accountId, autoFilled: false };

      // Auto-fill other entries with the same partyTaxId
      const entry = entries[index];
      if (entry?.partyTaxId) {
        for (let i = 0; i < updated.length; i++) {
          if (
            i !== index &&
            entries[i]?.partyTaxId === entry.partyTaxId &&
            !updated[i]!.accountId
          ) {
            updated[i] = { ...updated[i]!, accountId, autoFilled: true };
          }
        }
      }

      return updated;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Centralizar {entries.length} registros</DialogTitle>
          <DialogDescription>
            Asigne la cuenta {isPurchase ? "de gasto" : "de ingreso"} para cada
            documento. Los documentos con contraparte conocida se asignan
            automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 py-4">
          {entries.map((entry, i) => {
            const assignment = assignments[i];
            if (!assignment) return null;

            return (
              <div key={entry.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-mono">{entry.documentTypeCode}</span>{" "}
                    #{entry.documentNumber}
                    {" · "}
                    <span className="text-muted-foreground">
                      {entry.partyName ?? entry.partyTaxId ?? "Sin contraparte"}
                    </span>
                    {" · "}
                    <strong>{formatCLP(entry.totalAmount)}</strong>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Label className="text-xs text-muted-foreground w-16 shrink-0">
                    Cuenta:
                  </Label>
                  <select
                    className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-sm"
                    value={assignment.accountId}
                    onChange={(e) => handleAccountChange(i, e.target.value)}
                  >
                    <option value="">Seleccionar cuenta...</option>
                    {selectableAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} - {a.name}
                      </option>
                    ))}
                  </select>

                  {assignment.autoFilled && (
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      (auto)
                    </span>
                  )}

                  {entry.partyTaxId && !assignment.autoFilled && (
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap cursor-pointer">
                      <Checkbox
                        checked={assignment.saveMapping}
                        onCheckedChange={(checked) => {
                          setAssignments((prev) => {
                            const updated = [...prev];
                            updated[i] = {
                              ...updated[i]!,
                              saveMapping: !!checked,
                            };
                            return updated;
                          });
                        }}
                      />
                      Guardar
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Config accounts (read-only) */}
        <div className="border-t pt-3 space-y-1 text-sm text-muted-foreground">
          {isPurchase ? (
            <>
              <div>
                IVA CF:{" "}
                <strong className="text-foreground">
                  {configAccountLabel(accountingConfig?.inputVatAccountId)}
                </strong>
              </div>
              <div>
                Cta. por Pagar:{" "}
                <strong className="text-foreground">
                  {configAccountLabel(
                    accountingConfig?.accountsPayableAccountId,
                  )}
                </strong>
              </div>
            </>
          ) : (
            <>
              <div>
                IVA DF:{" "}
                <strong className="text-foreground">
                  {configAccountLabel(accountingConfig?.outputVatAccountId)}
                </strong>
              </div>
              <div>
                Cta. por Cobrar:{" "}
                <strong className="text-foreground">
                  {configAccountLabel(
                    accountingConfig?.accountsReceivableAccountId,
                  )}
                </strong>
              </div>
            </>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={createAsDraft}
              onCheckedChange={(checked) => setCreateAsDraft(!!checked)}
            />
            Crear como borrador
          </label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCentralizing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCentralize}
              disabled={!allAssigned || isCentralizing || accountsLoading}
            >
              {isCentralizing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Centralizando...
                </>
              ) : (
                `Centralizar (${entries.length})`
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
